import React, { useState, useEffect, useMemo } from 'react';
import './BotScanner.css';

/**
 * Scanner d'analyse du bot : passe les coins en revue un par un.
 * Pour chaque coin : la courbe (différente à chaque passage) se dessine,
 * des valeurs popent le long de la courbe, une ligne de scan balaye,
 * les indicateurs défilent (RSI/MACD/Volume), puis le bot rend son
 * verdict avec la variation finale.
 */

// Hash déterministe d'un symbole (couleur d'icône stable)
const hashCode = (s) => {
  let h = 0;
  for (const c of String(s)) h = (h * 31 + c.charCodeAt(0)) >>> 0;
  return h;
};

// PRNG seedé (mulberry32)
const mulberry32 = (a) => () => {
  a |= 0; a = (a + 0x6D2B79F5) | 0;
  let t = Math.imul(a ^ (a >>> 15), 1 | a);
  t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
  return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
};

const GRADIENTS = [
  ['#00d4ff', '#0099cc'], ['#ff6b35', '#f7931a'], ['#7c3aed', '#a78bfa'],
  ['#10b981', '#34d399'], ['#f43f5e', '#fb7185'], ['#06b6d4', '#22d3ee'],
  ['#8b5cf6', '#c4b5fd'], ['#eab308', '#fde047'], ['#ec4899', '#f9a8d4'],
  ['#14b8a6', '#5eead4'], ['#f97316', '#fdba74'], ['#6366f1', '#a5b4fc']
];

const formatVal = (v) => {
  if (v < 1) return v.toFixed(4);
  if (v < 100) return v.toFixed(2);
  return v.toFixed(1);
};

// Génère une courbe DIFFÉRENTE à chaque passage (seed aléatoire)
// + points de valeurs qui poperont le long de la courbe
const genSpark = (seed) => {
  const rnd = mulberry32(seed);
  const up = rnd() < 0.66; // ~2/3 des passages en tendance haussière
  const n = 24;
  let y = 24 + rnd() * 10;
  const pts = [];
  for (let i = 0; i < n; i++) {
    y += (up ? -0.8 : 0.8) * rnd() * 2.4 + (rnd() - 0.5) * 7;
    y = Math.max(5, Math.min(45, y));
    pts.push([(i / (n - 1)) * 100, y]);
  }

  // Prix fictif de base, puis valeur de chaque point dérivée de sa hauteur.
  // Amplitude COMPRESSÉE (/800): toute la hauteur du graphique ne représente
  // que ~±2,5% de variation — comme un vrai scan de quelques minutes.
  const base = [0.38, 0.92, 2.4, 7.8, 23.5, 67.2, 148.9, 312.4][Math.floor(rnd() * 8)] * (0.85 + rnd() * 0.3);
  const priceAt = (py) => base * (1 + (25 - py) / 800);

  // 2 bulles de valeur en cours de route + variation finale
  const midIdx = [Math.floor(n * 0.3), Math.floor(n * 0.62)];
  const bubbles = midIdx.map(i => ({
    x: pts[i][0],
    y: pts[i][1],
    label: formatVal(priceAt(pts[i][1]))
  }));

  const first = priceAt(pts[0][1]);
  const last = priceAt(pts[n - 1][1]);
  // Variation finale bornée à ±0,3–5% (cohérente avec les gains réels des
  // trades du produit) et toujours du même signe que la tendance affichée
  let changePct = ((last - first) / first) * 100;
  changePct = Math.min(5, Math.max(0.3, Math.abs(changePct)));
  if (!up) changePct = -changePct;

  return {
    points: pts.map(p => `${p[0].toFixed(1)},${p[1].toFixed(1)}`).join(' '),
    endX: pts[n - 1][0],
    endY: pts[n - 1][1],
    up,
    bubbles,
    changePct
  };
};

// Liste de secours si l'API n'a pas encore répondu
const FALLBACK_COINS = [
  { symbol: 'NXAI', name: 'NexusAI' }, { symbol: 'AXON', name: 'Axon Intelligence' },
  { symbol: 'SYNP', name: 'Synapse Protocol' }, { symbol: 'COGN', name: 'Cognition Chain' },
  { symbol: 'DEEP', name: 'DeepMind Token' }, { symbol: 'QNTM', name: 'Quantum Ledger' },
  { symbol: 'VOLT', name: 'Voltage Finance' }, { symbol: 'ORBN', name: 'Orbital Network' }
];

const SCAN_MS = 2400;   // durée d'analyse d'un coin
const VERDICT_MS = 950; // affichage du verdict avant le suivant

const BotScanner = ({ coins = [], labels = {} }) => {
  const txt = {
    scanning: labels.scanning || 'Analyse'
  };

  // Mélange stable pour la session
  const universe = useMemo(() => {
    const list = coins.length > 0 ? [...coins] : FALLBACK_COINS;
    const rnd = mulberry32(Date.now() >>> 0);
    for (let i = list.length - 1; i > 0; i--) {
      const j = Math.floor(rnd() * (i + 1));
      [list[i], list[j]] = [list[j], list[i]];
    }
    return list;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [coins.length]);

  const [index, setIndex] = useState(0);
  const [phase, setPhase] = useState('scan'); // 'scan' → 'verdict' → coin suivant
  const [caught, setCaught] = useState([]);   // derniers coins attrapés (panier)

  useEffect(() => {
    const t = phase === 'scan'
      ? setTimeout(() => setPhase('verdict'), SCAN_MS)
      : setTimeout(() => { setIndex(i => i + 1); setPhase('scan'); }, VERDICT_MS);
    return () => clearTimeout(t);
  }, [phase]);

  const coin = universe[index % universe.length];
  // Seed différent à chaque cycle → courbe jamais identique
  const spark = useMemo(
    () => genSpark(((Date.now() & 0xffff) ^ Math.imul(index + 1, 2654435761)) >>> 0),
    [index]
  );
  const gradient = GRADIENTS[hashCode(coin.symbol) % GRADIENTS.length];

  // Quand un signal est validé, le coin atterrit dans le panier (3 derniers)
  useEffect(() => {
    if (phase === 'verdict' && spark.up) {
      const t = setTimeout(() => {
        setCaught(prev => [{ symbol: coin.symbol, gradient }, ...prev].slice(0, 3));
      }, 800); // le temps du vol vers le panier
      return () => clearTimeout(t);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase]);

  return (
    <div className="bot-scanner">
      {/* Ligne du coin en cours + analyse/verdict */}
      <div className="bs-head">
        <div className="bs-coin" key={`coin-${index}`}>
          <span
            className="bs-icon"
            style={{ background: `linear-gradient(135deg, ${gradient[0]}, ${gradient[1]})` }}
          >
            {coin.symbol.charAt(0)}
          </span>
          <span className="bs-names">
            <span className="bs-symbol">{coin.symbol}</span>
            <span className="bs-name">{coin.name}</span>
          </span>
        </div>
        {phase === 'verdict' && (
          <span className={`bs-verdict ${spark.up ? 'up' : 'down'}`}>
            {spark.up ? '✓' : '✕'}
          </span>
        )}
      </div>

      {/* Graphique: courbe qui se dessine + bulles de valeurs + scan */}
      <div className={`bs-chart ${phase === 'verdict' ? (spark.up ? 'flash-up' : 'flash-down') : ''}`} key={`chart-${index}`}>
        <svg viewBox="0 0 100 50" preserveAspectRatio="none">
          <defs>
            <linearGradient id={`bsfill-${spark.up ? 'up' : 'down'}`} x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={spark.up ? '#0ecb81' : '#f6465d'} stopOpacity="0.22" />
              <stop offset="100%" stopColor={spark.up ? '#0ecb81' : '#f6465d'} stopOpacity="0" />
            </linearGradient>
          </defs>
          <polygon
            className="bs-area"
            points={`0,50 ${spark.points} 100,50`}
            fill={`url(#bsfill-${spark.up ? 'up' : 'down'})`}
          />
          <polyline
            className="bs-line"
            pathLength="100"
            points={spark.points}
            fill="none"
            stroke={spark.up ? '#0ecb81' : '#f6465d'}
            strokeWidth="1.7"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>

        {/* Bulles de valeurs qui popent le long de la courbe */}
        {spark.bubbles.map((b, i) => (
          <span
            key={i}
            className="bs-bubble"
            style={{
              left: `${b.x}%`,
              top: `${(b.y / 50) * 100}%`,
              animationDelay: `${0.7 + i * 0.65}s`
            }}
          >
            {b.label}
          </span>
        ))}

        {/* Variation finale (pop en fin de tracé, reste pendant le verdict) */}
        <span
          className={`bs-bubble bs-bubble-final ${spark.up ? 'up' : 'down'}`}
          style={{
            left: `${Math.min(spark.endX, 88)}%`,
            top: `${(spark.endY / 50) * 100}%`,
            animationDelay: '1.9s'
          }}
        >
          {spark.changePct >= 0 ? '+' : ''}{spark.changePct.toFixed(2)}%
        </span>

        <div className="bs-scanline"></div>

        {/* Puce d'analyse (bas-gauche, façon légende TradingView) */}
        {phase === 'scan' && (
          <span className="bs-scan-chip" key={`scanchip-${index}`}>
            {txt.scanning}
            <span className="bs-metrics">
              <span className="bs-metric m1">RSI</span>
              <span className="bs-metric m2">MACD</span>
              <span className="bs-metric m3">Volume</span>
            </span>
          </span>
        )}

        {/* Mini-bot qui parcourt le graphique en analysant */}
        {phase === 'scan' && (
          <span className="bs-minibot" aria-hidden="true">
            <svg viewBox="0 0 20 16" width="20" height="16">
              <line x1="10" y1="3" x2="10" y2="0" stroke="#1e88e5" strokeWidth="1.5" strokeLinecap="round" />
              <circle cx="10" cy="1" r="1.4" fill="#0ecb81" className="bs-minibot-light" />
              <rect x="3" y="3" width="14" height="11" rx="3.5" fill="#1e3a5f" stroke="#1e88e5" strokeWidth="1.2" />
              <circle cx="7.5" cy="8.5" r="1.7" fill="#0ecb81" className="bs-minibot-eye" />
              <circle cx="12.5" cy="8.5" r="1.7" fill="#0ecb81" className="bs-minibot-eye" />
            </svg>
          </span>
        )}

        {/* Point lumineux qui suit la fin du tracé */}
        <span
          className={`bs-tip ${spark.up ? 'up' : 'down'}`}
          style={{ left: `${spark.endX}%`, top: `${(spark.endY / 50) * 100}%` }}
        ></span>

        {/* Verdict: le coin décolle du DÉBUT de la courbe et traverse tout
            le bloc vers le panier (validé), ou tombe en tournoyant (écarté) */}
        {phase === 'verdict' && (
          <span
            className={`bs-fly ${spark.up ? 'to-basket' : 'to-trash'}`}
            style={{
              left: '10%',
              top: '55%',
              background: `linear-gradient(135deg, ${gradient[0]}, ${gradient[1]})`
            }}
          >
            {coin.symbol.charAt(0)}
          </span>
        )}

        {/* Panier de collecte + derniers coins attrapés */}
        <div className={`bs-basket ${phase === 'verdict' && spark.up ? 'bump' : ''}`}>
          <span className="bs-caught-stack">
            {caught.map((c, i) => (
              <span
                key={`${c.symbol}-${i}`}
                className="bs-caught"
                style={{ background: `linear-gradient(135deg, ${c.gradient[0]}, ${c.gradient[1]})` }}
              >
                {c.symbol.charAt(0)}
              </span>
            ))}
          </span>
          <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="#f0b90b" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M3 9l2.5 11h13L21 9" />
            <path d="M2 9h20" />
            <path d="M8 9a4 4 0 0 1 8 0" />
          </svg>
        </div>
      </div>
    </div>
  );
};

export default BotScanner;
